import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, Check, ArrowUp, ArrowDown, Trash2, Plus, X } from 'lucide-react';

/**
 * The job card's process table — one row per process, every number the shift
 * needs across the row, the way TrueERP prints it.
 *
 * The scan at the machine is how this fills itself in: it stamps the start, and
 * the second scan stamps the end, the output and the wastage. But a tablet goes
 * flat and a shift still has to be recorded, so every cell a person could need
 * is also writable in place — click a Machine or Operator cell to pick one,
 * click a number to type it. Start and End stay read-only; those are the two the
 * scan exists to get right.
 *
 * Design is the one row with no machine: it picks a designer instead, in the
 * same cell, and that designer is the row's person.
 *
 * A completed row locks. It is a record of what happened, not a form.
 */

const COLUMNS = [
  ['process', 'Process', 'left', 'w-40'],
  ['machine', 'Machine', 'left', 'w-28'],
  ['operator', 'Operator', 'left', 'w-24'],
  ['setup', 'Setup', 'right', 'w-16'],
  ['started_at', 'Start Time', 'center', 'w-20'],
  ['input_qty', 'Input Qty', 'right', 'w-20'],
  ['ended_at', 'End Time', 'center', 'w-20'],
  ['output_qty', 'Output Qty', 'right', 'w-20'],
  ['duration', 'Duration', 'right', 'w-20'],
  ['wastage', 'Wastage / %', 'right', 'w-24'],
  ['repairs', 'Repairs', 'right', 'w-16'],
  ['cartons', 'Cartons', 'right', 'w-16'],
  ['remarks', 'Remarks', 'left', ''],
];

/**
 * What the floor moved, as opposed to what a machine ran: the pcs that arrived
 * at the process and the pcs sent on from it, with anything the mover declared
 * short. Read from the movement ledger, shown to admins only.
 */
const FLOW_COLUMNS = [
  ['qty_received', 'Recd Qty', 'right', 'w-20'],
  ['qty_moved_on', 'Moved / Short', 'right', 'w-24'],
];

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };

// design is drawn by a person, so its row picks a designer where every other
// row picks a press
const DESIGN_STAGE_KEY = 'design';

const clock = (seconds) => {
  if (!seconds) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

const time = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

const STATE_STYLE = {
  running: 'bg-amber-50',
  completed: 'bg-emerald-50/60',
  pending: '',
};

/**
 * A cell you pick from. Used for Machine and Operator.
 *
 * It stays clickable whether or not a batch has reached the row and whether or
 * not anyone has linked machines to the stage — a supervisor books the press
 * before the work arrives, and a stage nobody has linked yet is the normal case
 * for a process that was only just added. Linked machines are listed first;
 * everything else follows under a divider.
 *
 * `onClear` puts a way back at the top of the list: the wrong press gets booked,
 * and taking it off must leave the process exactly where it was.
 */
function PickCell({ row, value, options, labelOf, onPick, testId, placeholder, disabled,
                    onClear, clearLabel }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (disabled || (options.length === 0 && !onClear)) {
    return <span className="block px-1.5 py-1 truncate text-slate-500">{value || ''}</span>;
  }

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const pick = (option) => run(() => onPick(row, option));

  const preferred = options.filter(o => o.linked);
  const rest = options.filter(o => !o.linked);
  const groups = preferred.length ? [[`For ${row.process}`, preferred], ['Other', rest]]
                                  : [[null, options]];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full px-1.5 py-1 text-left truncate hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-brand-blue focus:ring-inset ${
            value ? '' : 'text-slate-300'
          }`}
          data-testid={testId}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : value || placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 max-h-72 overflow-y-auto p-1" align="start">
        {onClear && (
          <button
            type="button"
            onClick={() => run(() => onClear(row))}
            className="w-full flex items-center gap-2 px-2 py-1.5 mb-1 text-xs text-left rounded border-b border-slate-100 text-slate-500 hover:bg-slate-100"
            data-testid={`${testId}-clear`}
          >
            <X className="h-3 w-3" /> {clearLabel}
          </button>
        )}
        {groups.map(([heading, list]) => list.length > 0 && (
          <div key={heading || 'all'}>
            {heading && (
              <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">{heading}</p>
            )}
            {list.map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => pick(option)}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs text-left rounded hover:bg-slate-100"
              >
                {labelOf(option)}
                {labelOf(option) === value && <Check className="h-3 w-3 text-emerald-600" />}
              </button>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A cell you type into. The scan is how these numbers are meant to arrive, but a
 * tablet goes flat and a shift still has to be written down, so the row stays
 * writable. Saves on blur or Enter; Escape puts it back.
 */
function EditCell({ row, field, value, onSave, align = 'right', type = 'number', disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (disabled) {
    return <span className={`block px-1.5 py-1 ${ALIGN[align]} tabular-nums`}>{value || ''}</span>;
  }

  const start = () => { setDraft(value ?? ''); setEditing(true); };

  const commit = async () => {
    setEditing(false);
    const next = type === 'number' ? parseInt(draft, 10) : draft;
    const unchanged = type === 'number'
      ? (Number.isNaN(next) ? value == null : next === value)
      : next === (value || '');
    if (unchanged) return;
    await onSave(row, field, type === 'number' ? (Number.isNaN(next) ? 0 : next) : next);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={start}
        className={`w-full px-1.5 py-1 ${ALIGN[align]} tabular-nums hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-brand-blue focus:ring-inset ${
          value ? '' : 'text-slate-300'
        }`}
        data-testid={`edit-${field}-${row.stage_key}`}
      >
        {value || '–'}
      </button>
    );
  }

  return (
    <input
      autoFocus
      type={type}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setEditing(false); }
      }}
      aria-label={`${row.process} ${field}`}
      className={`w-full px-1.5 py-1 ${ALIGN[align]} tabular-nums bg-white border border-brand-blue focus:outline-none`}
    />
  );
}

/**
 * Add a process the order never asked for — the job changed on the floor.
 *
 * Two answers, because both matter: which process, and where it goes. Appending
 * to the end and asking the supervisor to walk it up the list was a different
 * job every time work had already started, since nothing may move past work that
 * has happened. Only the processes this product type actually runs are offered —
 * an invented stage has no supervisor linked to it and no machine, so the job
 * would stand at a step nobody can see.
 */
function AddProcessRow({ available = [], rows = [], onAdd, busy }) {
  const [stageKey, setStageKey] = useState('');
  const [afterKey, setAfterKey] = useState('');

  if (!available.length) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!stageKey) return;
    await onAdd(stageKey, afterKey || null);
    setStageKey('');
    setAfterKey('');
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2 border-t border-slate-300 px-2 py-1.5">
      <Plus className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <select
        value={stageKey}
        onChange={e => setStageKey(e.target.value)}
        className="h-7 min-w-[10rem] rounded border border-slate-300 bg-white px-1.5 text-xs"
        data-testid="add-process-select"
      >
        <option value="">Add a process…</option>
        {available.map(rule => (
          <option key={rule.stage_key} value={rule.stage_key}>{rule.stage_name}</option>
        ))}
      </select>
      <span className="text-[11px] text-slate-500">after</span>
      <select
        value={afterKey}
        onChange={e => setAfterKey(e.target.value)}
        className="h-7 min-w-[10rem] rounded border border-slate-300 bg-white px-1.5 text-xs"
        data-testid="add-process-after"
      >
        <option value="">the start of the job</option>
        {rows.map(row => (
          <option key={row.stage_key} value={row.stage_key}>{row.process}</option>
        ))}
      </select>
      <button type="submit" disabled={busy || !stageKey}
        className="px-2.5 py-1 text-[11px] font-medium rounded bg-brand-blue text-white disabled:opacity-40">
        Add
      </button>
      <span className="text-[11px] text-slate-400">
        Goods already past that point keep going — they skip it.
      </span>
    </form>
  );
}

/** Who moved what, when — the detail behind a Moved / Short cell. */
const movementTitle = (row) => (row.movements || [])
  .map(m => [
    `${(m.qty_moved || 0).toLocaleString()} → ${m.to_stage_name || 'Completed'}`,
    m.qty_short ? `${m.qty_short.toLocaleString()} short` : null,
    m.direction === 'backward' ? 'sent back' : null,
    m.moved_by_name,
    m.moved_at ? new Date(m.moved_at).toLocaleString() : null,
    m.remarks || null,
  ].filter(Boolean).join(' · '))
  .join('\n');

export default function ProcessTable({
  rows = [], totals = {}, onAssignMachine, onAssignOperator, onClearMachine, onEditRow,
  canEdit = false, onMove, onRemove, onAdd, addableStages = [], busy = false, showFlow = false,
  designerOptions = [],
}) {
  const after = COLUMNS.findIndex(([key]) => key === 'output_qty') + 1;
  const base = showFlow
    ? [...COLUMNS.slice(0, after), ...FLOW_COLUMNS, ...COLUMNS.slice(after)]
    : COLUMNS;
  const columns = canEdit ? [...base, ['edit', '', 'center', 'w-16']] : base;

  return (
    <div className="border border-slate-300 flex flex-col min-h-0">
      <div className="overflow-auto min-h-0 flex-1">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-brand-blue text-white">
              {columns.map(([key, label, align, width]) => (
                <th key={key}
                  className={`${ALIGN[align]} ${width} px-1.5 py-1.5 font-semibold border-r border-blue-400 last:border-r-0 whitespace-nowrap`}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-2 py-8 text-center text-slate-400">
                  No production pipeline on this job card yet.
                </td>
              </tr>
            ) : rows.map((row, idx) => {
              // a finished process is a record of what happened, not a form
              const locked = !canEdit || row.state === 'completed';
              const isDesign = row.stage_key === DESIGN_STAGE_KEY;
              return (
              <tr key={row.stage_key || row.process}
                className={`border-b border-slate-200 ${STATE_STYLE[row.state] || ''}`}
                data-testid={`process-row-${row.stage_key}`}>
                <td className="px-1.5 py-1 font-medium text-slate-800 border-r border-slate-200 whitespace-nowrap">
                  <span className="text-slate-300 tabular-nums mr-1.5">{idx + 1}</span>
                  {row.process}
                </td>
                <td className="border-r border-slate-200 p-0"
                  title={isDesign ? 'Design runs on a designer, not a machine' : undefined}>
                  {isDesign ? (
                    <PickCell
                      row={row} value={row.operator} options={designerOptions}
                      labelOf={d => d.name} onPick={onAssignOperator}
                      onClear={row.machine && onClearMachine ? onClearMachine : undefined}
                      clearLabel="No machine"
                      testId={`designer-cell-${row.stage_key}`} placeholder="pick designer"
                      disabled={locked}
                    />
                  ) : (
                    <PickCell
                      row={row} value={row.machine} options={row.machine_options || []}
                      labelOf={m => m.machine_name} onPick={onAssignMachine}
                      onClear={row.machine && onClearMachine ? onClearMachine : undefined}
                      clearLabel="No machine"
                      testId={`machine-cell-${row.stage_key}`} placeholder="pick"
                      disabled={locked}
                    />
                  )}
                </td>
                <td className="border-r border-slate-200 p-0">
                  {isDesign ? (
                    <span className="block px-1.5 py-1 text-slate-300">–</span>
                  ) : (
                    <PickCell
                      row={row} value={row.operator} options={row.operator_options || []}
                      labelOf={o => o.name} onPick={onAssignOperator}
                      testId={`operator-cell-${row.stage_key}`} placeholder="pick"
                      disabled={locked}
                    />
                  )}
                </td>
                <td className="border-r border-slate-200 p-0">
                  {row.setup_seconds ? (
                    <span className="block px-1.5 py-1 text-right tabular-nums">{clock(row.setup_seconds)}</span>
                  ) : (
                    <EditCell row={row} field="setup_seconds" value={null}
                      onSave={(r, f, v) => onEditRow(r, f, v * 60)} disabled={locked} />
                  )}
                </td>
                <td className="px-1.5 py-1 text-center border-r border-slate-200 tabular-nums">{time(row.started_at)}</td>
                <td className="border-r border-slate-200 p-0">
                  <EditCell row={row} field="input_qty" value={row.input_qty || null}
                    onSave={onEditRow} disabled={locked} />
                </td>
                <td className="px-1.5 py-1 text-center border-r border-slate-200 tabular-nums">{time(row.ended_at)}</td>
                <td className="border-r border-slate-200 p-0">
                  <EditCell row={row} field="output_qty" value={row.output_qty || null}
                    onSave={onEditRow} disabled={locked} />
                </td>
                {showFlow && (
                  <>
                    <td className="px-1.5 py-1 text-right border-r border-slate-200 tabular-nums text-slate-600"
                      data-testid={`recd-qty-${row.stage_key}`}>
                      {row.qty_received ? row.qty_received.toLocaleString() : ''}
                    </td>
                    <td className="px-1.5 py-1 text-right border-r border-slate-200 tabular-nums"
                      title={movementTitle(row)} data-testid={`moved-qty-${row.stage_key}`}>
                      {row.qty_moved_on ? row.qty_moved_on.toLocaleString() : ''}
                      {row.qty_short > 0 && (
                        <span className="text-red-600"> · {row.qty_short.toLocaleString()}</span>
                      )}
                    </td>
                  </>
                )}
                <td className="px-1.5 py-1 text-right border-r border-slate-200 tabular-nums">{clock(row.turnaround_seconds)}</td>
                <td className="px-1.5 py-1 text-right border-r border-slate-200 tabular-nums">
                  {row.wastage_qty ? `${row.wastage_qty} · ${row.wastage_pct}%` : ''}
                </td>
                <td className="border-r border-slate-200 p-0">
                  <EditCell row={row} field="repairs" value={row.repairs || null}
                    onSave={onEditRow} disabled={locked} />
                </td>
                <td className="border-r border-slate-200 p-0">
                  <EditCell row={row} field="cartons" value={row.cartons || null}
                    onSave={onEditRow} disabled={locked} />
                </td>
                <td className="p-0">
                  <EditCell row={row} field="remarks" value={row.remarks} type="text" align="left"
                    onSave={onEditRow} disabled={locked} />
                </td>
                {canEdit && (
                  <td className="px-1 py-0.5 border-l border-slate-200">
                    <div className="flex items-center justify-center gap-0.5">
                      <button type="button" disabled={busy || idx === 0}
                        onClick={() => onMove(idx, -1)} aria-label={`Move ${row.process} earlier`}
                        className="text-slate-400 hover:text-brand-blue disabled:opacity-25">
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button type="button" disabled={busy || idx === rows.length - 1}
                        onClick={() => onMove(idx, 1)} aria-label={`Move ${row.process} later`}
                        className="text-slate-400 hover:text-brand-blue disabled:opacity-25">
                        <ArrowDown className="h-3 w-3" />
                      </button>
                      <button type="button" disabled={busy || row.state !== 'pending'}
                        onClick={() => onRemove(row)} aria-label={`Remove ${row.process}`}
                        title={row.state !== 'pending' ? 'Already worked on' : 'Remove this process'}
                        className="text-slate-400 hover:text-red-500 disabled:opacity-25">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {canEdit && <AddProcessRow available={addableStages} rows={rows} onAdd={onAdd} busy={busy} />}

      {/* setup + productive + unproductive = turnaround, the way the card totals it */}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-300 bg-slate-50 px-2 py-1.5 text-xs">
        {[
          ['Set Up Time', totals.setup_seconds, 'bg-red-500'],
          ['Productive Time', totals.productive_seconds, 'bg-brand-blue'],
          ['UnProductive Time', totals.unproductive_seconds, 'bg-red-400'],
        ].map(([label, seconds, tone], idx) => (
          <React.Fragment key={label}>
            {idx > 0 && <span className="text-slate-400 font-bold">+</span>}
            <span className="flex items-stretch">
              <span className={`${tone} text-white px-2 py-0.5 font-medium rounded-l`}>{label}</span>
              <span className="border border-l-0 border-slate-300 bg-white px-2 py-0.5 tabular-nums rounded-r min-w-[4rem] text-right">
                {clock(seconds) || '—'}
              </span>
            </span>
          </React.Fragment>
        ))}
        <span className="text-slate-400 font-bold">=</span>
        <span className="flex items-stretch">
          <span className="bg-brand-blue text-white px-2 py-0.5 font-medium rounded-l">Turn Around Time</span>
          <span className="border border-l-0 border-slate-300 bg-white px-2 py-0.5 tabular-nums rounded-r min-w-[4rem] text-right font-semibold"
            data-testid="turnaround-total">
            {clock(totals.turnaround_seconds) || '—'}
          </span>
        </span>
      </div>
    </div>
  );
}
